import React, { useEffect, useState } from 'react';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { getCompanyCodeFromSessionStorage, getAccountIdFromSessionStorage, formatAddress } from '../lib/global_function';
import CompanyApi from '../company/companyApi';
import { Company } from '../company/companyType';
import { Send, Mail, MessageSquare } from 'lucide-react';
import AccountApi from './accountApi';
import { validateEmail, validateAndFormatPhoneNumber } from '../lib/validator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert } from '../components/ui/alert';

const AccountInvitePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Account', path: '/main/account' },
    { label: 'Account Invitation', path: '/main/customer/Account Invitation' },
  ];

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [message, setMessage] = useState<string | null>(null);
  const [company, setCompany] = useState<Company | undefined>(undefined);
  const [sendMethod, setSendMethod] = useState<'email' | 'text' | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [accountId, setAccountId] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [isSentSuccessfully, setIsSentSuccessfully] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      const companyCode = getCompanyCodeFromSessionStorage(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
      const accountId = getAccountIdFromSessionStorage(process.env.REACT_APP_SESSION_STORAGE_KEY as string);

      if (!companyCode) {
        setErrorMessage('Company not found.');
        return;
      }
      if (!accountId) {
        setErrorMessage('Account not found.');
        return;
      } else {
        setAccountId(accountId);
      }

      try {
        const fetchedCompany = await CompanyApi.getCompanyByCode(companyCode);
        setCompany(fetchedCompany);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };
    fetchCompany();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    setIsSentSuccessfully(false);

    if (!sendMethod) {
      setErrorMessage('Please select a send method');
      setStatus('error');
      return;
    }
    if (!accountId) {
      setErrorMessage('Account not found.');
      setStatus('error');
      return;
    }

    if (!company?.company_code) {
      setErrorMessage('Company not found.');
      setStatus('error');
      return;
    }

    if (sendMethod === 'email' && !email) {
      setErrorMessage('Please enter an email address.');
      setStatus('error');
      return;
    }

    if (sendMethod === 'text' && !phone) {
      setErrorMessage('Please enter a phone number.');
      setStatus('error');
      return;
    }

    try {
      await AccountApi.sendAccountInvite(
        accountId,
        company.company_code,
        sendMethod === 'text' ? 'sms' : sendMethod,
        email,
        phone
      );
      setStatus('success');
      setMessage('An link for opening an account has been sent out.');
      setIsSentSuccessfully(true);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setErrorMessage('');

    switch (name) {
      case 'phone': {
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || 'Invalid phone number');
        } else {
          setPhone(phoneResult.value);
        }
        break;
      }
      case 'email': {
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || 'Invalid email address format');
        } else {
          setEmail(emailResult.value);
        }
        break;
      }
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage('');
    }
  };

  const handleSendMethodChange = (method: 'email' | 'text') => {
    setErrorMessage('');
    setSendMethod(method);
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-bold text-foreground mt-6 mb-4">Send Account Invitation</h2>

        {message ? (
          <Alert variant="success">
            {message}
          </Alert>
        ) : (
          <>
            {errorMessage && (
              <Alert variant="error">
                {errorMessage}
              </Alert>
            )}

<form onSubmit={handleSubmit}>
  <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card ">
    {company && (
      <div className="space-y-4">
        <p className="text-base text-foreground">
          You can send an invitation as a link for anyone in your company to open an online account.
        </p>

        <p className="text-base text-foreground">
          The online account wiull be associated with your company: <strong>{company.name}</strong>{' '}
          {/** 
        {formatAddress(company.address_1, company.address_2, company.city, company.state, company.zip)},{' '}
        {company.phone}
        */}
        </p>

        <p className="text-base text-foreground">
          The link will be expired in 24 hours.
        </p>

        <p className="text-base text-foreground font-medium mt-5">
          Select either Email or Text below, and click Send button.
        </p>

        <div className="space-y-5">
          <div className="flex gap-6 mt-5">
            <label className="flex items-center gap-2 text-base font-medium text-foreground cursor-pointer">
              <input
                type="radio"
                value="email"
                checked={sendMethod === 'email'}
                onChange={() => handleSendMethodChange('email')}
                className="size-4 cursor-pointer"
              />
              <Mail className="size-5" />
              Email
            </label>

            <label className="flex items-center gap-2 text-base font-medium text-foreground cursor-pointer">
              <input
                type="radio"
                value="text"
                checked={sendMethod === 'text'}
                onChange={() => handleSendMethodChange('text')}
                className="size-4 cursor-pointer"
              />
              <MessageSquare className="size-5" />
              Text
            </label>
          </div>

          {sendMethod === 'email' && (
            <div>
              <Label htmlFor="email" className="text-base">
                Recipient Email Address: <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                onBlur={handleBlur}
                required
                className="mt-2"
              />
            </div>
          )}

          {sendMethod === 'text' && (
            <div>
              <Label htmlFor="phone" className="text-base">
                Recipient Phone Number: <span className="text-destructive">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter phone number"
                onBlur={handleBlur}
                required
                className="mt-2"
              />
            </div>
          )}
        </div>
      </div>
    )}
  </div>

  {company && (
    <div className="mt-5 max-w-2xl">
      <Button
        type="submit"
        size="medium"
        variant="default"
        disabled={status === 'loading'}
      >
        <Send className="size-5" />
        {status === 'loading'
          ? 'Sending...'
          : `Send${sendMethod ? ` ${sendMethod === 'email' ? 'Email' : 'Text'}` : ''}`}
      </Button>
    </div>
  )}
</form>
          </>
        )}
      </div>
    </>
  );
};

export default AccountInvitePage;